import {GitBranchCheckIcon} from '@github-ui/pacer/CustomIcon'
import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {CopilotIcon, GitBranchIcon} from '@primer/octicons-react'
import {IconButton, SkeletonBox} from '@primer/react'
import {useCallback, useRef} from 'react'

import type {AgentDropdownRepository} from '../../utils/copilot-chat-types'
import {copilotLocalStorage} from '../../utils/copilot-local-storage'
import {CustomAgentSelector} from './CustomAgentSelector'
import {IconButtonSelector} from './IconButtonSelector'
import styles from './RepoBranchAgentSelector.module.css'

type RepoBranchAgentPickerProps = {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  selectedRepo: AgentDropdownRepository | undefined
  selectedBranch: string | undefined
  selectedCustomAgent: string | undefined

  onRepoSelect: (repo: AgentDropdownRepository | undefined) => void
  onBranchSelect: (branch: string | undefined) => void
  onCustomAgentSelect: (agent: string | undefined) => void

  agentSelectionEnabled?: boolean
}

export const RepoBranchAgentPicker = ({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  selectedRepo,
  selectedBranch,
  selectedCustomAgent,
  onRepoSelect,
  onBranchSelect,
  onCustomAgentSelect,
  agentSelectionEnabled = true,
}: RepoBranchAgentPickerProps) => {
  // Track whether we've already done the initial auto-selection
  const hasAutoSelectedRef = useRef(false)

  const onLoad = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      // Only auto-select on the initial load when no repo is selected and we haven't auto-selected before
      let initialRepo: AgentDropdownRepository | undefined
      if (!selectedRepo && !hasAutoSelectedRef.current) {
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo?.enabled) {
            onRepoSelect(initialRepo)
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo?.enabled) {
            onRepoSelect(loadedLastUsedRepo)
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }

        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onRepoSelect(firstEnabledRepo)
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        } else if (initialRepo) {
          // The initial repo is disabled, but we have no enabled choices
          onRepoSelect(initialRepo)
          onBranchSelect(initialBranch || initialRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [selectedRepo, initialRepoNWO, onRepoSelect, onBranchSelect, initialBranch],
  )

  return (
    <div className={styles.container}>
      {!hideRepoSelector && (
        <ReposSelector<AgentDropdownRepository>
          repositoryLoader={(query, defaultOwner) => queryForRepos(query, defaultOwner, initialRepoNWO)}
          selectionVariant="single"
          selectAllOption={false}
          onSelect={repo => {
            if (repo) {
              onRepoSelect(repo)
              onBranchSelect(repo.defaultBranch)
            }
          }}
          currentSelection={selectedRepo}
          width="medium"
          additionalButtonProps={{
            'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
            className: styles.repoSelectorButton,
          }}
          onLoad={onLoad}
          loadingElement={<SkeletonBox width="150px" height="32px" />}
        />
      )}
      <>
        {!selectedRepo ? (
          <IconButton icon={GitBranchIcon} aria-label="Loading branches…" disabled />
        ) : (
          <RefSelector
            // `cacheKey` is something consumers of this component usually get from a key derived from a current repository in the app payload
            // In our case, it's a static string since this page is not aware of any kind of current repository
            cacheKey="repo-branch-agent-selector"
            canCreate={false}
            currentCommitish={selectedBranch ?? selectedRepo.defaultBranch}
            defaultBranch={selectedRepo.defaultBranch}
            owner={selectedRepo.owner}
            repo={selectedRepo.nameOnly}
            types={['branch']}
            selectedRefType="branch"
            closeOnSelect
            hideShowAll
            actionListHeadingText="Select a branch"
            onSelectItem={onBranchSelect}
            renderAnchor={props => (
              <IconButtonSelector
                {...props}
                icon={GitBranchIcon}
                selectedIcon={GitBranchCheckIcon}
                selected={selectedBranch !== selectedRepo.defaultBranch}
                aria-label={`Branch: ${selectedBranch || 'Select branch'}`}
                aria-labelledby={undefined}
              />
            )}
          />
        )}
      </>

      {agentSelectionEnabled &&
        (!selectedRepo ? (
          <IconButton icon={CopilotIcon} aria-label="Loading agents…" disabled />
        ) : (
          <CustomAgentSelector
            nwo={selectedRepo.nameWithOwner}
            branch={selectedBranch}
            selectedAgent={selectedCustomAgent}
            onSelect={onCustomAgentSelect}
          />
        ))}
    </div>
  )
}

const COPILOT_REPOSITORIES_API_ENDPOINT = '/copilot/agent-sessions/repositories'

interface AgentRepositoryResponse {
  id: number
  graphQLId: string
  nameWithOwner: string
  enabled: boolean
  name: string
  owner: string
  defaultBranch: string
  visibility: string
  isEmpty: boolean
  userHasRequestedCca?: boolean
  userCanRequestCca?: boolean
  userCanGrantOrgCca?: boolean
  userCanGrantEnterpriseCca?: boolean
  businessSlug?: string
}

/**
 * Shared function to fetch Copilot-enabled repositories from the API
 */
export async function queryForRepos(
  query: string = '',
  defaultOwner?: string,
  repoToIncludeInTopRepos?: string,
): Promise<AgentDropdownRepository[]> {
  const url = new URL(COPILOT_REPOSITORIES_API_ENDPOINT, window.location.origin)

  if (query.trim()) {
    url.searchParams.set('q', query.trim())
  }

  if (defaultOwner) {
    url.searchParams.set('defaultOwner', defaultOwner)
  }

  if (repoToIncludeInTopRepos) {
    url.searchParams.set('includeInTopRepos', repoToIncludeInTopRepos)
  }

  const response = await verifiedFetchJSON(url.href)

  if (response.ok) {
    const {repositories} = (await response.json()) as {repositories: AgentRepositoryResponse[]}
    return repositories.map((repo: AgentRepositoryResponse) => {
      return {
        id: repo.id,
        graphQLId: repo.graphQLId,
        nameWithOwner: repo.nameWithOwner,
        enabled: repo.enabled,
        name: repo.nameWithOwner,
        nameOnly: repo.name,
        owner: repo.owner,
        defaultBranch: repo.defaultBranch,
        private: repo.visibility !== 'public',
        isEmpty: repo.isEmpty,
        userHasRequestedCca: repo.userHasRequestedCca,
        userCanRequestCca: repo.userCanRequestCca,
        userCanGrantOrgCca: repo.userCanGrantOrgCca,
        userCanGrantEnterpriseCca: repo.userCanGrantEnterpriseCca,
        businessSlug: repo.businessSlug,
      }
    })
  }
  return []
}

try{ RepoBranchAgentPicker.displayName ||= 'RepoBranchAgentPicker' } catch {}